import { useEffect, useState } from 'react';

export type GoogleMapsReadiness = 'loading' | 'ready' | 'unavailable';

const POLL_INTERVAL_MS = 250;
const GIVE_UP_AFTER_MS = 10000;
/** Slower watch after giving up, so a late script still recovers the map. */
const RECOVERY_INTERVAL_MS = 2000;

/**
 * Whether the Maps SDK is usable yet.
 *
 * The SDK is injected once at app start (`maybeAddGooglePlacesScriptTag`) and
 * gives no completion signal here, so readiness is polled — and bounded, so a
 * script that never arrives ends as `unavailable` instead of a permanent
 * spinner. Callers must render something useful without a map.
 */
export const useGoogleMapsReady = (): GoogleMapsReadiness => {
  const [readiness, setReadiness] = useState<GoogleMapsReadiness>(() =>
    window.google?.maps ? 'ready' : 'loading'
  );

  useEffect(() => {
    if (readiness === 'ready') return undefined;

    // After the cap the UI shows its fallback, but the watch continues at a
    // slower cadence: a script that arrives at 10.5s should still get a map
    // rather than leaving the page permanently degraded.
    if (readiness === 'unavailable') {
      const recovery = setInterval(() => {
        if (window.google?.maps) {
          setReadiness('ready');
          clearInterval(recovery);
        }
      }, RECOVERY_INTERVAL_MS);
      return () => clearInterval(recovery);
    }

    const startedAt = Date.now();
    const timer = setInterval(() => {
      if (window.google?.maps) {
        setReadiness('ready');
        clearInterval(timer);
        return;
      }
      if (Date.now() - startedAt > GIVE_UP_AFTER_MS) {
        setReadiness('unavailable');
        clearInterval(timer);
      }
    }, POLL_INTERVAL_MS);

    return () => clearInterval(timer);
  }, [readiness]);

  return readiness;
};
